/**
 * Firebase Emulator Connection Service
 * Manages dynamic connection to Firebase emulators for testing
 * Note: Emulator connections must happen during Firebase initialization,
 * so this service manages localStorage state and triggers page reloads
 */
class EmulatorService {
  constructor() {
    this.isConnectedToEmulator = false;
    this.emulatorSettings = {
      firestore: { host: '127.0.0.1', port: 8080 },
      auth: { url: 'http://127.0.0.1:9099' },
      functions: { host: '127.0.0.1', port: 5002 },
      storage: { host: '127.0.0.1', port: 9199 }
    };
  }

  /**
   * Connect to Firebase emulators (requires page reload)
   */
  async connectToEmulators() {
    // Check if already connected
    const currentStatus = this.getConnectionStatus();
    if (currentStatus.isConnected) {
      return { success: true, message: 'Already connected to emulators' };
    }

    try {
      // First check if emulators are running
      const isAvailable = await this.checkEmulatorAvailability();
      if (!isAvailable) {
        return { 
          success: false, 
          error: 'Firebase emulators are not running. Please start them with: firebase emulators:start' 
        };
      }

      // Store connection preference in localStorage
      if (typeof window !== 'undefined') {
        localStorage.setItem('firebase_emulator_connected', 'true');
        
        // Reload the page to reinitialize Firebase with emulator connections
        window.location.reload();
      }

      return { 
        success: true, 
        message: 'Emulator connection enabled. Page will reload to complete the connection.' 
      };

    } catch (error) {
      console.error('Error connecting to emulators:', error);
      return { 
        success: false, 
        error: `Failed to connect to emulators: ${error.message}` 
      };
    }
  }

  /**
   * Disconnect from emulators (requires page reload)
   */
  async disconnectFromEmulators() {
    try {
      // Remove connection state from localStorage
      if (typeof window !== 'undefined') {
        localStorage.removeItem('firebase_emulator_connected');
        
        // Reload the page to reinitialize Firebase with production connections
        window.location.reload();
      }

      this.isConnectedToEmulator = false;

      return { 
        success: true, 
        message: 'Emulator connection disabled. Page will reload to connect to production.' 
      };
    } catch (error) {
      console.error('Error disconnecting from emulators:', error);
      return { 
        success: false, 
        error: `Failed to disconnect from emulators: ${error.message}` 
      };
    }
  }

  /**
   * Force production mode (emergency override)
   * This will work even if emulators are running or if there are connection issues
   */
  forceProductionMode() {
    try {
      if (typeof window !== 'undefined') {
        // Clear all emulator-related localStorage items
        localStorage.removeItem('firebase_emulator_connected');
        localStorage.removeItem('firebase_emulator_override');
        
        // Set production override flag
        localStorage.setItem('firebase_force_production', 'true');
        
        // Clear the flag after a short delay so it doesn't persist forever
        setTimeout(() => {
          localStorage.removeItem('firebase_force_production');
        }, 1000);
        
        // Reload immediately to production mode
        window.location.reload();
      }

      return { 
        success: true, 
        message: 'Forcing production mode. Page will reload to production Firebase.' 
      };
    } catch (error) {
      console.error('Error forcing production mode:', error);
      return { 
        success: false, 
        error: `Failed to force production mode: ${error.message}` 
      };
    }
  }

  /**
   * Check if currently connected to emulators
   */
  getConnectionStatus() {
    const localStorageStatus = typeof window !== 'undefined' ? 
      localStorage.getItem('firebase_emulator_connected') === 'true' : false;
    
    return {
      isConnected: this.isConnectedToEmulator || localStorageStatus,
      settings: this.emulatorSettings
    };
  }

  /**
   * Check if emulators are available (running)
   */
  async checkEmulatorAvailability() {
    try {
      // Try to fetch the emulator UI (port 4400)
      const response = await fetch(`http://${this.emulatorSettings.firestore.host}:4400`, {
        method: 'GET',
        mode: 'no-cors' // Avoid CORS issues
      });
      return true; // If no error thrown, emulators are running
    } catch (error) {
      // Try alternative method - check Firestore emulator directly
      try {
        const firestoreResponse = await fetch(`http://${this.emulatorSettings.firestore.host}:${this.emulatorSettings.firestore.port}`, {
          method: 'GET',
          mode: 'no-cors'
        });
        return true;
      } catch (firestoreError) {
        console.log('Emulators not detected as running');
        return false;
      }
    }
  }
}

export const emulatorService = new EmulatorService();